/**
 * OPO — Cornelius retention proposals (Tier-3 D3).
 */
import React, { useState, useEffect, useCallback } from 'react';

const inputStyle = {
  padding: '7px 12px',
  borderRadius: 'var(--border-radius)',
  border: '1px solid var(--border-color)',
  background: 'var(--surface-color)',
  color: 'var(--text-primary)',
  fontSize: 13,
};

const card = {
  background: 'var(--card-bg)',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--border-radius)',
  padding: '12px 14px',
  marginBottom: 10,
  color: 'var(--text-primary)',
};

function btn(variant) {
  const styles = {
    primary: { background: 'var(--primary-color)', color: 'var(--text-on-primary)' },
    danger: { background: 'transparent', color: 'var(--danger-color)', border: '1px solid var(--danger-color)' },
    ghost: { background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' },
  };
  return {
    padding: '6px 12px',
    borderRadius: 'var(--border-radius)',
    border: 'none',
    fontSize: 12,
    cursor: 'pointer',
    fontWeight: 600,
    ...styles[variant || 'primary'],
  };
}

export default function AdminCorneliusRetention() {
  const [userId, setUserId] = useState('13');
  const [decision, setDecision] = useState('open');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!userId.trim()) return;
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`/api/opo/cornelius-memories/retention/proposals?user_id=${encodeURIComponent(userId.trim())}&decision=${decision}`);
      const d = await r.json();
      if (!r.ok || d.success === false) throw new Error(d.error || `HTTP ${r.status}`);
      setRows(d.proposals || []);
    } catch (e) {
      setError(e.message);
      setRows([]);
    }
    setLoading(false);
  }, [userId, decision]);

  useEffect(() => { load(); }, [load]);

  async function decide(id, choice) {
    setBusyId(id);
    try {
      const r = await fetch(`/api/opo/cornelius-memories/retention/proposals/${id}/decide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision: choice }),
      });
      const d = await r.json();
      if (!r.ok || d.success === false) throw new Error(d.error || `HTTP ${r.status}`);
      await load();
    } catch (e) {
      setError(e.message);
    }
    setBusyId(null);
  }

  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={{ marginBottom: 14, color: 'var(--text-secondary)', fontSize: 13 }}>
        Proposals emitted by the weekly memory janitor. Decisions other than <em>defer</em> may purge or
        flag memories on <code>assistant_memory</code>.
      </div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        <input value={userId} onChange={e => setUserId(e.target.value)} placeholder="User ID" style={{ ...inputStyle, width: 160 }} />
        <select value={decision} onChange={e => setDecision(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
          <option value="open">Open</option>
          <option value="all">All</option>
          <option value="purge">Purge</option>
          <option value="forget">Forget</option>
          <option value="approve_keep">Approved (keep)</option>
          <option value="defer">Deferred</option>
        </select>
        <button onClick={load} style={btn('primary')}>Refresh</button>
      </div>

      {error && <div style={{ color: 'var(--danger-color)', marginBottom: 12, fontSize: 13 }}>{error}</div>}
      {loading && <div style={{ color: 'var(--text-secondary)' }}>Loading…</div>}
      {!loading && rows.length === 0 && (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>No proposals to show.</div>
      )}

      {rows.map((p) => (
        <div key={p.id} style={card}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong>#{p.id}</strong>
            <code style={{ fontSize: 11 }}>{p.proposed_action}</code>
            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
              user {p.user_id} • memory {p.memory_id} • {p.age_days}d old • {p.database_name}
            </span>
            {p.decision && (
              <span style={{
                fontSize: 11,
                padding: '2px 6px',
                borderRadius: 6,
                background: 'var(--surface-color)',
                border: '1px solid var(--border-color)',
              }}>{p.decision}</span>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{p.reason}</div>
          {!p.decided_at && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <button disabled={busyId === p.id} onClick={() => decide(p.id, 'approve_keep')} style={btn('primary')}>Keep</button>
              <button disabled={busyId === p.id} onClick={() => decide(p.id, 'forget')} style={btn('ghost')}>Soft forget</button>
              <button disabled={busyId === p.id} onClick={() => decide(p.id, 'purge')} style={btn('danger')}>Hard purge</button>
              <button disabled={busyId === p.id} onClick={() => decide(p.id, 'defer')} style={btn('ghost')}>Defer</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
